import { setSeatCount } from '@saaga/shared/store/slices/billing.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { microsToDollars } from '@saaga/shared/utils/billing.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useEffect } from 'react'

import type { BillingSeatInterval, SeatInfo } from '@/components/billing/billing-seat.interface'

import { useAddSeats } from './use-add-seats.hook'
import { type RemoveSeatsResult, useRemoveSeats } from './use-remove-seats.hook'
import { useSeatsApi } from './use-seats-api.hook'
import { useSeatsState } from './use-seats-state.hook'

export interface UseSeatsReturn {
  seatInfo: SeatInfo | null
  isLoading: boolean
  isAddingSeats: boolean
  isRemovingSeats: boolean
  error: string | null
  addSeats: (quantity: number) => Promise<boolean>
  removeSeats: (quantity: number, currentMemberCount: number) => Promise<RemoveSeatsResult>
  refetch: () => Promise<void>
}

const transformBillingInterval = (interval: 'month' | 'year'): BillingSeatInterval => interval

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

export const useSeats = (usedSeats: number): UseSeatsReturn => {
  const dispatch = useAppDispatch()
  const { getSeats } = useSeatsApi()
  const state = useSeatsState()
  const { setSeatInfo, setIsLoading, setError, setIsAddingSeats, setIsRemovingSeats } = state

  const fetchSeats = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getSeats()
      setSeatInfo({
        allowedSeats: response.allowedSeats,
        additionalSeats: response.additionalSeats,
        totalSeats: response.totalSeats,
        usedSeats,
        seatPrice: microsToDollars(response.seatPrice),
        billingInterval: transformBillingInterval(response.billingInterval),
      })
      dispatch(setSeatCount(response.totalSeats))
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load seat information'))
      logger.error('Failed to fetch seats', { error: err })
    } finally {
      setIsLoading(false)
    }
  }, [getSeats, usedSeats, setSeatInfo, setIsLoading, setError, dispatch])

  useEffect(() => {
    void fetchSeats()
  }, [fetchSeats])

  const { addSeats } = useAddSeats({ setIsAddingSeats, fetchSeats })
  const { removeSeats } = useRemoveSeats({
    seatInfo: state.seatInfo,
    setIsRemovingSeats,
    fetchSeats,
  })

  return {
    seatInfo: state.seatInfo,
    isLoading: state.isLoading,
    isAddingSeats: state.isAddingSeats,
    isRemovingSeats: state.isRemovingSeats,
    error: state.error,
    addSeats,
    removeSeats,
    refetch: fetchSeats,
  }
}
